<template>
  <div v-show="hasBannerContent" class="bloomreach-banner-wrapper space-y-3">
    <div
      v-for="slotId in BLOOMREACH_MAX_BANNERS_PER_PLACEMENT"
      :id="getBannerSlotId(slotId)"
      :key="slotId"
      class="bloomreach-banner-container"
    />
    <Teleport to="body">
      <div
        class="fixed left-4 top-[calc(var(--status-bar-height)+64px)] z-[1000] flex w-[calc(100%-32px)] justify-center"
      >
        <FreeAlert ref="copyAlertRef" :duration="2000">
          <template #icon>
            <img
              src="@/assets/images/green-copy-icon.svg"
              width="24"
              height="24"
              alt=""
              role="presentation"
            />
          </template>
          <p class="text-text-neutral-primary">
            {{ $t('common.promocodeCopied') }}
          </p>
        </FreeAlert>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, onUnmounted } from 'vue';
  import { useRouter } from 'vue-router';
  import { FreeAlert } from 'freedom-shared';
  // @ts-expect-error No type declarations found
  import { copyText } from '@aviata/copy-text';

  import {
    BLOOMREACH_MAX_BANNERS_PER_PLACEMENT,
    type BannerPlacement,
  } from '@/shared/lib';

  interface BannerClickDetail {
    url?: string;
    promocode?: string;
    placement: string;
    bannerId: string;
    bannerName: string;
  }

  const props = defineProps<{
    placement: BannerPlacement;
  }>();

  const router = useRouter();

  const hasBannerContent = ref(false);
  const copyAlertRef = ref<InstanceType<typeof FreeAlert> | null>(null);

  let observer: MutationObserver | null = null;

  const getBannerSlotId = (slotId: number) =>
    `bloomreach-banner-${props.placement}-${slotId}`;

  const checkForContent = () => {
    for (let i = 1; i <= BLOOMREACH_MAX_BANNERS_PER_PLACEMENT; i++) {
      const container = document.getElementById(getBannerSlotId(i));
      if (container && container.children.length > 0) {
        hasBannerContent.value = true;
        return;
      }
    }
    hasBannerContent.value = false;
  };

  const handleBannerClick = (event: Event) => {
    const customEvent = event as CustomEvent<BannerClickDetail>;
    const { url, promocode } = customEvent.detail;

    if (promocode) {
      copyText(promocode).then(() => {
        copyAlertRef.value?.show();
      });
    } else if (url) {
      router.push(url);
    }
  };

  onMounted(() => {
    const containers: HTMLElement[] = [];

    for (let i = 1; i <= BLOOMREACH_MAX_BANNERS_PER_PLACEMENT; i++) {
      const container = document.getElementById(getBannerSlotId(i));
      if (container) {
        containers.push(container);
        container.addEventListener(
          'bloomreach-banner-click',
          handleBannerClick,
        );
      }
    }

    if (containers.length === 0) return;

    checkForContent();

    observer = new MutationObserver(() => {
      checkForContent();
    });

    containers.forEach(container => {
      observer!.observe(container, {
        childList: true,
        subtree: true,
      });
    });
  });

  onUnmounted(() => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }

    for (let i = 1; i <= BLOOMREACH_MAX_BANNERS_PER_PLACEMENT; i++) {
      const container = document.getElementById(getBannerSlotId(i));
      if (container) {
        container.removeEventListener(
          'bloomreach-banner-click',
          handleBannerClick,
        );
      }
    }
  });
</script>
